package com.imobly.imobly.ui.screens.show.showlease

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Money
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.compose.rememberNavController
import com.imobly.imobly.domain.Lease
import com.imobly.imobly.ui.components.button.ButtonComp
import com.imobly.imobly.ui.components.searchbar.SearchBarComp
import com.imobly.imobly.ui.components.title.TitleComp
import com.imobly.imobly.ui.components.topbar.TopBarComp
import com.imobly.imobly.ui.theme.colors.BackGroundColor
import com.imobly.imobly.ui.theme.colors.CancelColor
import com.imobly.imobly.ui.theme.colors.ConfirmColor
import com.imobly.imobly.ui.theme.colors.PrimaryColor
import com.imobly.imobly.ui.theme.colors.TitleColor
import com.imobly.imobly.ui.theme.fonts.montserratFont
import com.imobly.imobly.viewmodel.LeaseViewModel
import org.jetbrains.compose.ui.tooling.preview.Preview

@Composable
fun ShowLeasesScreen(leaseViewModel: LeaseViewModel) {
    LaunchedEffect(Unit) {
        leaseViewModel.findAllAction()
    }

    Scaffold(
        topBar = { TopBarComp() },
        snackbarHost = {
            SnackbarHost(leaseViewModel.snackMessage.value)
        },
        contentWindowInsets = WindowInsets.systemBars,
    ) { paddingValues ->
        Column(
            modifier = Modifier.background(BackGroundColor).padding(paddingValues).fillMaxSize()
        ) {
            TitleComp(text = "Contratos", buttonBackAction = { leaseViewModel.goToHome() })

            Spacer(modifier = Modifier.height(8.dp))

            Box(
                modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center
            ) {
                ButtonComp(
                    "Cadastrar contrato",
                    { Icon(Icons.Default.Add, "Sinal de soma") },
                    PrimaryColor,
                    { leaseViewModel.goToCreateLease() }
                )
            }

            Box(
                modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center
            ) {
                SearchBarComp(
                    "Buscar contratos pelo nome do locatário ou do imóvel",
                    leaseViewModel.searchText.value,
                    { leaseViewModel.changeSearchText(it) },
                    { leaseViewModel.searchAction() }
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            LazyColumn(
                verticalArrangement = Arrangement.spacedBy(12.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.fillMaxWidth().weight(1f),
                contentPadding = PaddingValues(horizontal = 8.dp, vertical = 8.dp)
            ) {
                items(leaseViewModel.leases.value) { lease ->
                    LeaseCard(
                        lease = lease,
                        leaseViewModel = leaseViewModel,
                    )
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            val totalPages = leaseViewModel.totalPages.value
            if (totalPages > 1) {
                Box(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                    contentAlignment = Alignment.Center
                ) {
                    LazyRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        items(totalPages) { pageIndex ->
                            val isSelected = leaseViewModel.currentPage.value == pageIndex
                            Surface(
                                onClick = { leaseViewModel.loadPage(pageIndex) },
                                shape = RoundedCornerShape(50),
                                color = if (isSelected) PrimaryColor else Color.White,
                                shadowElevation = 2.dp,
                                modifier = Modifier.size(40.dp),
                                border = if (!isSelected) BorderStroke(1.dp, Color(0xFFCCCCCC)) else null
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    Text(
                                        text = "${pageIndex + 1}",
                                        color = if (isSelected) Color.White else TitleColor,
                                        fontFamily = montserratFont(),
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 14.sp
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun LeaseCard(lease: Lease, leaseViewModel: LeaseViewModel) {
    val hasOverdue = lease.overdueInstallmentsCount > 0
    Card(
        modifier = Modifier
            .padding(horizontal = 4.dp, vertical = 4.dp)
            .widthIn(max = 700.dp)
            .fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = if (hasOverdue) Color(0xFFFFF5F5) else Color.White),
        border = if (hasOverdue) BorderStroke(1.5.dp, CancelColor) else null,
        shape = RoundedCornerShape(16.dp), elevation = CardDefaults.cardElevation(4.dp)
    ) {
        Column(
            modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            TextInfoComp("Imóvel", lease.property.title)
            TextInfoComp("Locatário", lease.tenant.firstName)
            TextInfoComp("Data de início", lease.startDate)
            TextInfoComp("Dia do vencimento", lease.paymentDueDay)
            TextInfoComp("Aluguel", lease.monthlyRent)
            TextInfoComp("Deposíto", lease.securityDeposit)
            TextInfoComp("Parcelas atrasadas", "${lease.overdueInstallmentsCount}", color = if (hasOverdue) CancelColor else ConfirmColor)
            TextInfoComp("Criado em", lease.createdAt)
            TextInfoComp("Última vez atualizado em", lease.lastUpdatedAt)

            Spacer(Modifier.height(8.dp))

            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                ButtonComp(
                    "Atualizar dados",
                    { Icon(Icons.Default.Edit, "Atualizar dados") },
                    PrimaryColor,
                    { leaseViewModel.goToEditLease(lease) }
                )
                ButtonComp(
                    "Ver pagamentos",
                    { Icon(Icons.Default.Money, "Ver pagamentos") },
                    PrimaryColor,
                    { leaseViewModel.goToShowPayments(lease) }
                )
            }
        }
    }
}

@Composable
fun TextInfoComp(label: String, value: String, color: Color = Color.Black) {
    Row(Modifier.padding(2.dp)) {
        Text(
            "${label}:  ",
            color = Color.Black,
            fontSize = 14.sp,
            textAlign = TextAlign.Center,
            fontFamily = montserratFont(),
            fontWeight = FontWeight.SemiBold
        )

        Text(
            value,
            color = color,
            fontSize = 14.sp,
            textAlign = TextAlign.Center,
            fontFamily = montserratFont(),
            fontWeight = FontWeight.Medium
        )
    }
}

@Preview
@Composable
fun ShowLeasesPreview() {
    val navControllerFake = rememberNavController()
    ShowLeasesScreen(LeaseViewModel(navControllerFake))
}
